// 1st/2nd Half Goals — bands view (Phase 3, HTML <canvas>).
//
// The only canvas widget. React owns the wrapper + team label + <canvas>; a
// useEffect does all the 2D drawing (a verbatim port of the legacy draw: two
// timeline sections of goal "dots" sized by count, with per-half totals), wires
// the tooltip hit-testing (mousemove over the dot circles), and a ResizeObserver
// — all cleaned up in the effect return. The A/B compares the rendered bitmap,
// so the draw math must match the legacy exactly. No enter/exit animation here
// (the legacy redraws the whole canvas each time).

import { useEffect, useRef } from "react";
import { selectHalfGoalsBands, HALF_GOALS_H1_LABELS, HALF_GOALS_H2_LABELS, type HalfGoalsBandsWindow } from "@bento/shared";
import { useWidgetData } from "../../data/useWidgetData.js";
import { useFixtureKey } from "../../data/useFixtureKey.js";
import WidgetSkeleton from "./WidgetSkeleton.js";
import WidgetError from "./WidgetError.js";

interface Circle {
  x: number;
  y: number;
  r: number;
  label: string;
}

export interface HalfGoalsBandsProps {
  fixture: string;
  team?: string | null;
  timeline?: string | null;
}

export default function HalfGoalsBands({
  fixture,
  team,
  timeline,
}: HalfGoalsBandsProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const labelRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const circlesRef = useRef<Circle[]>([]);

  // Both view children fetch the one `half-goals` envelope (shared key, deduped);
  // this child reads `bands`.
  const { data, isPending, isError, refetch } = useWidgetData(
    "half-goals",
    useFixtureKey(fixture)
  );
  const parts = (fixture || "Arsenal v Everton").split(" v ");
  const chosen = team || (parts[0] ?? "").trim();
  const td = selectHalfGoalsBands(data?.bands ?? {}, chosen, timeline);
  const sig = JSON.stringify(td);

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    const labelEl = labelRef.current;
    if (!canvas || !wrap || !labelEl || !td) return;
    const body = wrap.parentElement;
    if (!body) return;

    // jsdom throws on getContext (no canvas support) — bail so tests just assert
    // the skeleton; in the browser this is a real 2D context.
    let ctx: CanvasRenderingContext2D | null;
    try {
      ctx = canvas.getContext("2d");
    } catch {
      return;
    }
    if (!ctx) return;
    const c = ctx;

    const draw = (data: HalfGoalsBandsWindow) => {
      const PAD = 15;
      const BW = Math.max(140, body.clientWidth - PAD * 2);
      const BH = Math.max(80, body.clientHeight - PAD * 2);
      const LABEL_H = labelEl.offsetHeight || 20;
      const CVH = Math.max(60, BH - LABEL_H);

      const dpr = window.devicePixelRatio || 1;
      canvas.width = BW * dpr;
      canvas.height = CVH * dpr;
      canvas.style.width = BW + "px";
      canvas.style.height = CVH + "px";
      c.scale(dpr, dpr);

      const circles: Circle[] = [];
      circlesRef.current = circles;

      const GAP = Math.max(10, Math.round(BW * 0.055));
      const HALF_W = (BW - GAP) / 2;
      const BAND = HALF_W / 10;
      const SEC_H = CVH / 2;
      const AXIS_IN_SEC = SEC_H * 0.58;
      const LBL_IN_SEC = Math.min(AXIS_IN_SEC - 18, SEC_H * 0.22);
      const FOOT_IN_SEC = Math.min(SEC_H - 4, AXIS_IN_SEC + SEC_H * 0.32);

      const allVals = [...data.scored.h1, ...data.scored.h2, ...data.conceded.h1, ...data.conceded.h2];
      const maxVal = Math.max(...allVals, 1);
      const vertRoom = Math.min(AXIS_IN_SEC - LBL_IN_SEC - 8, FOOT_IN_SEC - AXIS_IN_SEC - 4);
      const MAX_R = Math.min(BAND * 0.95, vertRoom, 24);
      const MIN_R = Math.max(2, MAX_R * 0.18);
      const rOf = (v: number) =>
        v === 0 ? 0 : MIN_R + ((v - 1) / Math.max(maxVal - 1, 1)) * (MAX_R - MIN_R);

      const bx = (half: number, i: number) =>
        (half === 1 ? 0 : HALF_W + GAP) + BAND * i + BAND / 2;
      const midH1 = HALF_W / 2;
      const midH2 = HALF_W + GAP + HALF_W / 2;

      const htX = Math.floor(HALF_W + GAP / 2) + 0.5;
      c.strokeStyle = "rgba(255,255,255,0.18)";
      c.lineWidth = 1;
      c.beginPath();
      c.moveTo(htX, 0);
      c.lineTo(htX, CVH);
      c.stroke();

      const drawSection = (
        h1Data: number[],
        h2Data: number[],
        axisY: number,
        lblY: number,
        footY: number,
        col: string,
        sectionLabel: string,
        tot1: number,
        tot2: number
      ) => {
        c.strokeStyle = "#2a2a2a";
        c.lineWidth = 1;
        c.beginPath();
        c.moveTo(0, axisY);
        c.lineTo(HALF_W, axisY);
        c.moveTo(HALF_W + GAP, axisY);
        c.lineTo(BW, axisY);
        c.stroke();

        const dots: { x: number; r: number; v: number; band: string }[] = [];
        ([[h1Data, 1], [h2Data, 2]] as [number[], number][]).forEach(([data2, half]) => {
          const bandLabels = half === 1 ? HALF_GOALS_H1_LABELS : HALF_GOALS_H2_LABELS;
          data2.forEach((v, i) => {
            if (v) dots.push({ x: bx(half, i), r: rOf(v), v, band: bandLabels[i]! });
          });
        });
        dots.sort((a, b) => a.r - b.r);

        dots.forEach(({ x, r, v, band }) => {
          c.beginPath();
          c.arc(x, axisY, r, 0, Math.PI * 2);
          const grad = c.createRadialGradient(x, axisY, 0, x, axisY, r);
          grad.addColorStop(0, col + "cc");
          grad.addColorStop(1, col + "22");
          c.fillStyle = grad;
          c.fill();
          c.strokeStyle = col + "55";
          c.lineWidth = 1;
          c.stroke();
          circles.push({ x, y: axisY, r, label: band + " · " + v + (v === 1 ? " goal" : " goals") });
        });

        const fs = 11;
        const ffs = 9;
        c.textBaseline = "middle";
        c.font = `400 ${fs}px -apple-system, BlinkMacSystemFont, sans-serif`;
        c.fillStyle = "#888";
        c.textAlign = "left";
        c.fillText(sectionLabel, 0, lblY);

        c.font = `600 ${fs}px -apple-system, BlinkMacSystemFont, sans-serif`;
        c.fillStyle = col;
        c.textAlign = "center";
        const totAll = tot1 + tot2 || 1;
        c.fillText(tot1 + " (" + Math.round((tot1 / totAll) * 100) + "%)", midH1, lblY);
        c.fillText(tot2 + " (" + Math.round((tot2 / totAll) * 100) + "%)", midH2, lblY);

        c.font = `400 ${ffs}px -apple-system, BlinkMacSystemFont, sans-serif`;
        c.fillStyle = "#888";
        c.fillText("1H", midH1, footY);
        c.fillText("2H", midH2, footY);
      };

      const sum = (a: number[]) => a.reduce((x, y) => x + y, 0);
      const sc1t = sum(data.scored.h1);
      const sc2t = sum(data.scored.h2);
      const co1t = sum(data.conceded.h1);
      const co2t = sum(data.conceded.h2);

      drawSection(data.scored.h1, data.scored.h2, AXIS_IN_SEC, LBL_IN_SEC, FOOT_IN_SEC, "#2ecc71", "Scored", sc1t, sc2t);
      drawSection(data.conceded.h1, data.conceded.h2, SEC_H + AXIS_IN_SEC, SEC_H + LBL_IN_SEC, SEC_H + FOOT_IN_SEC, "#e74c3c", "Conceded", co1t, co2t);
    };

    draw(td);

    const onMove = (e: MouseEvent) => {
      const circles = circlesRef.current;
      const mx = e.offsetX;
      const my = e.offsetY;
      let hit: Circle | null = null;
      for (let i = circles.length - 1; i >= 0; i--) {
        const cc = circles[i]!;
        const dx = mx - cc.x;
        const dy = my - cc.y;
        if (dx * dx + dy * dy <= cc.r * cc.r) {
          hit = cc;
          break;
        }
      }
      const w = window as unknown as {
        showTooltipAt?: (t: string, x: number, y: number) => void;
        hideTooltip?: () => void;
      };
      if (hit) w.showTooltipAt?.(hit.label, e.clientX, e.clientY);
      else w.hideTooltip?.();
    };
    const onLeave = () => {
      (window as unknown as { hideTooltip?: () => void }).hideTooltip?.();
    };
    canvas.addEventListener("mousemove", onMove);
    canvas.addEventListener("mouseleave", onLeave);

    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => draw(td));
      ro.observe(body);
    }
    return () => {
      canvas.removeEventListener("mousemove", onMove);
      canvas.removeEventListener("mouseleave", onLeave);
      ro?.disconnect();
    };
    // `sig` captures the band data (the complete draw input); td read fresh.
  }, [sig]);

  if (isPending) return <WidgetSkeleton />;
  if (isError) return <WidgetError onRetry={refetch} />;

  if (!td) {
    return (
      <div className="data-wrapper top-align">
        <div className="module-empty">No data</div>
      </div>
    );
  }

  return (
    <div className="data-wrapper top-align hg-bands-wrapper" ref={wrapRef}>
      <div className="sc-team-label" ref={labelRef}>
        {chosen}
      </div>
      <canvas className="hg-bands-canvas" ref={canvasRef} />
    </div>
  );
}
